/// <amd-module name="Build/Admin/Common/Sources/SecuritySource" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssSecurityService } from "VSS/Platform/Security";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { DefinitionResourceReference } from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";

import { ITfsPageData, ITfsPageService } from "Tfs/Platform/Page";

const AgentQueueToken: string = "AgentQueues";
const AgentPoolToken: string = "AgentPools";
const DteSecurityNamespaceId: string = "101EAE8C-1709-47F9-B228-0E476C35B3BA";
const NamespaceSeparator: string = "/";
const AgentQueueProjectResourceType: string = "queue";
const AgentPoolResourceType: string = "pool";

export class SecuritySource {
    private _securityService: IVssSecurityService;
    private _buildClient: IBuildRestClient;
    private _tfsPageData: ITfsPageData | undefined;

    constructor(pageContext: IVssPageContext) {
        this._securityService = pageContext.getService<IVssSecurityService>("IVssSecurityService");
        this._buildClient = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        this._tfsPageData = tfsPageService.getData();
    }

    public hasAgentQueuePermission(queueId: number, permission: number): boolean {
        return this._securityService.hasPermission(DteSecurityNamespaceId, this._getAgentQueueSecurityToken(queueId), permission);
    }

    public hasAllAgentQueuesPermission(permission: number): boolean {
        return this._securityService.hasPermission(DteSecurityNamespaceId, this._getAgentQueueSecurityToken(undefined), permission);
    }

    public hasAgentPoolPermission(poolId: number, permission: number): boolean {
        return this._securityService.hasPermission(DteSecurityNamespaceId, this._getAgentPoolSecurityToken(poolId), permission);
    }

    public hasAllAgentPoolsPermission(permission: number): boolean {
        return this._securityService.hasPermission(DteSecurityNamespaceId, this._getAgentPoolSecurityToken(undefined), permission);
    }

    public async getPipelinesAuthorizedForQueue(taskAgentQueueId: number): Promise<boolean> {
        if (this._tfsPageData && this._tfsPageData.project) {
            return this._buildClient.getProjectResources(this._tfsPageData.project.id, AgentQueueProjectResourceType, taskAgentQueueId.toString()).then(
                (resourceReferences: DefinitionResourceReference[]) => {
                    return this._handleResourceReferenceResponse(taskAgentQueueId, resourceReferences);
                }
            );
        }
        else {
            return Promise.resolve(false);
        }
    }

    public async setPipelinesAuthorizedForQueue(agentQueue: TaskAgentQueue, authorizeAllPipelines: boolean): Promise<boolean> {
        if (this._tfsPageData && this._tfsPageData.project) {
            const reference: DefinitionResourceReference = {
                authorized: authorizeAllPipelines,
                id: agentQueue.id.toString(),
                name: agentQueue.name,
                type: AgentQueueProjectResourceType
            };

            return this._buildClient.authorizeProjectResources([reference], this._tfsPageData.project.id).then(
                (resourceReferences: DefinitionResourceReference[]) => {
                    return this._handleResourceReferenceResponse(agentQueue.id, resourceReferences);
                }
            );
        }
        else {
            return Promise.resolve(false);
        }
    }

    private _handleResourceReferenceResponse(taskAgentQueueId: number, resourceReferences: DefinitionResourceReference[]): boolean {
        const matchingReference: DefinitionResourceReference[] = resourceReferences.filter(reference => reference.id === taskAgentQueueId.toString());
        if (matchingReference[0]) {
            return matchingReference[0].authorized;
        }
        else {
            return false;
        }
    }

    private _getAgentQueueSecurityToken(queueId: number | undefined): string {
        let token: string = AgentQueueToken;

        if (this._tfsPageData && this._tfsPageData.project) {
            token = token.concat(NamespaceSeparator, this._tfsPageData.project.id);
        }

        if (queueId) {
            token = token.concat(NamespaceSeparator, queueId.toString());
        }

        return token;
    }

    private _getAgentPoolSecurityToken(poolId: number | undefined): string {
        let token: string = AgentPoolToken;

        if (poolId) {
            token = token.concat(NamespaceSeparator, poolId.toString());
        }

        return token;
    }
}
